/**
 * Iter270.1 — Centralized page-meta config.
 *
 * Single source of truth for `<title>` + meta description + canonical
 * path on the top 30+ public pages.
 *
 * Convention (carried over from /app/frontend/src/hooks/usePageMeta.js):
 *   - title:        short, end with " — QorTrace", < 60 chars total.
 *   - description:  1 sentence, < 160 chars, sells the click.
 *   - canonicalPath leading slash, no query string, no trailing slash
 *                   (except homepage which is "/").
 */
const M = {
  home: {
    title: "QorTrace — Post-Quantum Risk Intelligence for the OnChain Economy",
    description:
      "Free PQC wallet scan, full BOM audits, live trust posture, and Q-Day threat intelligence — across 18 chains. QorTrace is post-quantum risk intelligence for the onchain economy.",
    canonicalPath: "/",
  },
  freeScan: {
    title: "Free PQC Wallet Scan — QorTrace",
    description:
      "Get your wallet's post-quantum readiness score in seconds. Free, no card required, 18 chains supported including Bitcoin, Ethereum, Solana, Cosmos.",
    canonicalPath: "/free-scan",
  },
  scan: {
    title: "Run a PQC Scan — QorTrace",
    description:
      "Scan a wallet, contract, or chain address for post-quantum cryptographic risk. Bitcoin, Ethereum, Solana, Cosmos, and 14 more.",
    canonicalPath: "/scan",
  },
  bulkScan: {
    title: "Bulk PQC Scan — QorTrace",
    description:
      "Run hundreds of wallet PQC scans in a single batch. CSV-in, JSON-out, full audit trail.",
    canonicalPath: "/scan/bulk",
  },
  audit: {
    title: "PQC Audits — Quantum-Safe Code Review — QorTrace",
    description:
      "Have QorTrace's auditors hand-review your contracts, MPC setups, and cryptographic dependencies. Independent attestation, signed report, public verify URL.",
    canonicalPath: "/audit",
  },
  auditPreview: {
    title: "Audit Preview — QorTrace",
    description:
      "See an example QorTrace PQC audit: scoring rubric, severity pills, fix recommendations, signed verify URL.",
    canonicalPath: "/audit/preview",
  },
  auditTalkToSales: {
    title: "Talk to a Quantum-Safe Audit Engineer — QorTrace",
    description:
      "Schedule a 30-min consult with the QorTrace audit team. Scope your engagement, pricing, and timeline.",
    canonicalPath: "/audit/talk-to-sales",
  },
  atlas: {
    title: "Atlas — Treasury & Crypto Asset Monitoring — QorTrace",
    description:
      "Continuous post-quantum monitoring for institutional wallets, multisigs, and crypto treasuries. Live exposure score, alerts, evidence-grade PDF reports.",
    canonicalPath: "/atlas",
  },
  pricingHub: {
    title: "Pricing — QorTrace",
    description:
      "Pick your QorTrace surface: Wallet PQC Scanner, PQC Audit Suite, MPC Vendor Matrix, or Atlas Treasury Monitoring.",
    canonicalPath: "/pricing",
  },
  pricingScanner: {
    title: "PQC Scanner Pricing — QorTrace",
    description:
      "Per-seat pricing for the QorTrace Wallet PQC Scanner. 18-chain coverage, monthly or annual billing, with team-tier support.",
    canonicalPath: "/pricing/scanner",
  },
  pricingEnterprise: {
    title: "PQC Audit Suite Pricing — QorTrace",
    description:
      "Enterprise-tier PQC Audit Suite — hand-led audits, signed reports, public verify URLs, and re-audit cycles included.",
    canonicalPath: "/pricing/enterprise",
  },
  pricingMonitoring: {
    title: "Atlas Treasury Monitoring + MPC Vendor Matrix Pricing — QorTrace",
    description:
      "Continuous monitoring tiers for institutional treasuries. Atlas + MPC Vendor Matrix bundle pricing, annual contracts.",
    canonicalPath: "/pricing/monitoring",
  },
  pricingAudits: {
    title: "PQC Audit Packages — QorTrace",
    description:
      "Fixed-scope audit packages — Single Engagement, Re-Audit, and Continuous Audit Subscription. Public verify URL on every audit.",
    canonicalPath: "/pricing/audits",
  },
  trust: {
    title: "Trust Center — QorTrace",
    description:
      "Live 9-layer security posture for QorTrace itself — TLS, HSTS, CSP, DNSSEC, MTA-STS, audit-log integrity, evidence freshness, vulnerability scans, PQC readiness. Refreshed every minute.",
    canonicalPath: "/trust",
  },
  status: {
    title: "Status — QorTrace",
    description:
      "Live status of every QorTrace subsystem: API, database, scanner, audit engine, threat-intel ingest, verify, WAF.",
    canonicalPath: "/status",
  },
  momentum: {
    title: "Momentum — QorTrace",
    description:
      "Live scan velocity across QorTrace — total wallets scanned, chain coverage, PQC exposure trends. Refreshed every minute, fully anonymized.",
    canonicalPath: "/momentum",
  },
  methodology: {
    title: "Methodology — How QorTrace Scores Post-Quantum Risk",
    description:
      "Read the QorTrace audit methodology v0.2 — every signature scheme, KEM, hash, and curve we score, with sources and rationale.",
    canonicalPath: "/methodology",
  },
  threatRadar: {
    title: "Threat Radar — Live PQC Threat Intelligence — QorTrace",
    description:
      "QorTrace's live PQC threat radar: KEV catalog, GHSA advisories, high-severity NVD CVEs filtered for cryptographic relevance. Refreshed every 15 minutes.",
    canonicalPath: "/threat-radar",
  },
  exposedWallets: {
    title: "Exposed Wallets — QorTrace",
    description:
      "Public PQC exposure index — wallets whose addresses are known to be vulnerable to quantum-era cryptographic attacks. Aggregate counts only, fully anonymized.",
    canonicalPath: "/threat-radar/exposed-wallets",
  },
  threatModel: {
    title: "Threat Model — QorTrace",
    description:
      "QorTrace's auditor-facing threat model: assumptions, in-scope attackers, out-of-scope risks, and the mitigation map.",
    canonicalPath: "/threat-model",
  },
  trends: {
    title: "Community Pulse — Public PQC Trends — QorTrace",
    description:
      "What the QorTrace community is scanning, auditing, and worrying about this week. Public, anonymized, no PII.",
    canonicalPath: "/trends",
  },
  whatsNew: {
    title: "What's New — QorTrace",
    description:
      "Every QorTrace product release, sorted by date. Filter by tag (feature, developer, billing, ux), deep-link any entry.",
    canonicalPath: "/whats-new",
  },
  releases: {
    title: "Releases — QorTrace",
    description:
      "QorTrace public release log — versioned changelog, dated and tagged for every customer-facing change.",
    canonicalPath: "/releases",
  },
  qDay: {
    title: "Q-Day Tax Clock — Countdown to the Quantum Threat — QorTrace",
    description:
      "Live countdown to Q-Day — the day a cryptographically-relevant quantum computer makes today's wallets harvestable. Embeddable widget included.",
    canonicalPath: "/q-day",
  },
  sisterBrands: {
    title: "QorTrace Family — QorBOM, QorTrace Labs — QorTrace",
    description:
      "The QorTrace family of brands: QorBOM™ Cryptographic Bill-of-Materials, QorTrace Labs research, and the flagship QorTrace platform.",
    canonicalPath: "/sister-brands",
  },
  helpCenter: {
    title: "Help Center — QorTrace",
    description:
      "Find guides, troubleshooting articles, and integration walkthroughs for every QorTrace surface — scanner, audits, Atlas, and APIs.",
    canonicalPath: "/help",
  },
  signin: {
    title: "Sign in — QorTrace",
    description: "Sign in to your QorTrace account. New here? Create a free account in 30 seconds.",
    canonicalPath: "/signin",
  },
  signup: {
    title: "Create your QorTrace account",
    description:
      "Get a free QorTrace account — PQC wallet scans, audit history, threat intel, all in one workspace.",
    canonicalPath: "/signup",
  },
  compare: {
    title: "Compare PQC Posture — QorTrace",
    description:
      "Run two wallet PQC scans side-by-side and share the comparison. Free, no signup required.",
    canonicalPath: "/compare",
  },
  labs: {
    title: "QorTrace Labs — Applied Post-Quantum Research",
    description:
      "QorTrace's applied PQC research arm — manifestos, methodologies, vendor matrix, and engagement playbooks. Independent, public, evidence-led.",
    canonicalPath: "/labs",
  },
  labsManifesto: {
    title: "Labs Manifesto — QorTrace",
    description:
      "QorTrace Labs' principles: independent research, public methodology, no vendor capture, no security theater.",
    canonicalPath: "/labs/manifesto",
  },
  labsMethodology: {
    title: "Labs Methodology — QorTrace",
    description:
      "The QorTrace Labs research methodology — review cadence, vendor scoring rubric, conflict-of-interest disclosures, and reproducibility statement.",
    canonicalPath: "/labs/methodology",
  },
  labsEngagements: {
    title: "Labs Engagements — QorTrace",
    description:
      "Active QorTrace Labs research engagements: vendor reviews, PQC standard interpretation, and bespoke commissioned research.",
    canonicalPath: "/labs/engagements",
  },
  labsMpcMatrix: {
    title: "MPC Vendor Matrix — QorTrace Labs",
    description:
      "QorTrace Labs' independent comparison of MPC wallet vendors — PQC readiness, governance, audit history, and operational maturity.",
    canonicalPath: "/labs/mpc-vendor-matrix",
  },
  wallOfShipped: {
    title: "Wall of Shipped — QorTrace Labs",
    description:
      "Every artifact QorTrace Labs has shipped — research notes, vendor reviews, PQC migration playbooks, and free tools.",
    canonicalPath: "/labs/wall-of-shipped",
  },
  legalTerms: {
    title: "Terms of Service — QorTrace",
    description:
      "QorTrace's customer terms of service. Plain-language sections, no dark patterns, fully versioned with public change history.",
    canonicalPath: "/legal/terms",
  },
  legalPrivacy: {
    title: "Privacy Policy — QorTrace",
    description:
      "How QorTrace handles your data: collection, retention, subprocessors, and your rights. GDPR + CCPA aligned.",
    canonicalPath: "/legal/privacy",
  },
  legalCookies: {
    title: "Cookies Policy — QorTrace",
    description:
      "Which cookies QorTrace uses, what they do, and how to control them. Granular consent, never sold to third parties.",
    canonicalPath: "/legal/cookies",
  },
  legalSecurity: {
    title: "Security — QorTrace",
    description:
      "QorTrace's security posture: SOC 2 evidence, vulnerability disclosure policy, encryption-in-transit and at-rest controls.",
    canonicalPath: "/legal/security",
  },

  // ─── QorBOM (iter272.2) — full SEO parity with QorTrace ───────────
  // QorBOM pages can be reached two ways:
  //   - On qorbom.com directly (the canonical brand domain).
  //   - On qortrace.com under /qorbom/* (admin / cross-link access).
  // We use ABSOLUTE `canonical` (not `canonicalPath`) so the canonical
  // always points to qorbom.com regardless of which host the visitor
  // arrived on — preventing duplicate-content / wrong-base bugs.
  qorbomHome: {
    title: "QorBom — The CBOM Platform for Audit Firms",
    description:
      "Generate signed, white-labeled Cryptographic Bill-of-Materials in 30 seconds. Built for audit firms and procurement teams aligned with NIST FIPS 203/204/205, EU CRA, and SOC 2.",
    canonical: "https://qorbom.com/",
  },
  qorbomTrust: {
    title: "QorBom Trust & Compliance — NIST FIPS, EU CRA, SOC 2",
    description:
      "QorBom's live trust posture: NIST FIPS 203/204/205 alignment, EU Cyber Resilience Act readiness, SOC 2 evidence, and signed-CBOM verifiability for procurement.",
    canonical: "https://qorbom.com/trust",
  },
  qorbomApiReference: {
    title: "QorBom API Reference — OpenAPI 3.1",
    description:
      "Interactive OpenAPI 3.1 reference for the QorBom partner API. Generate, sign, and embed Cryptographic Bills-of-Materials directly into your audit deliverables.",
    canonical: "https://qorbom.com/api-reference",
  },
  qorbomApiDocs: {
    title: "QorBom Developer Docs — Build CBOMs into your audits",
    description:
      "Start-to-finish QorBom developer guide. SDKs, code samples, signing flow, white-label branding, and incident-response webhooks for audit firms.",
    canonical: "https://qorbom.com/developers",
  },
  qorbomPartnerPortal: {
    title: "QorBom Partner Portal — Branding, Spend, API Keys",
    description:
      "Manage your firm's QorBom branding, API keys, monthly spend, and signed-CBOM delivery webhooks. The control center for QorBom partners.",
    canonical: "https://qorbom.com/portal",
  },
  qorbomBecomePartner: {
    title: "Become a QorBom Partner — Audit-firm CBOM platform",
    description:
      "Apply to become a QorBom audit-firm partner. White-labeled signed CBOMs, NIST-aligned methodology, drop-in audit deliverables. 30-day evaluation included.",
    canonical: "https://qorbom.com/become-a-partner",
  },
};

export default M;
